export interface OrganisationClaim {
    id: string;
    name: string;
    activeLicenses: ActiveLicenses[];
}

export interface User {
    homeAccountId: string;
    name: string;
    username: string;
    givenName: any | '';
    familyName: any | '';
    fullName: string;
    email: string;
    role: Role;
    organisation: OrganisationClaim;
    accessToAllStores: boolean;
    shopIds: string[];
    organisationClaims?: OrganisationClaim[];
}

// Shop interface - Extended

export interface DataCollectionPeriod {
    dayOfWeek: number;
    from: string;
    until: string;
    isDataCollectionDisabled: boolean;
}

export interface SensorCameraCredentials {
    username: string;
    password: string;
}

export interface Shop {
    id: string;
    name: string;
    description: string;
    externalSystemShopIdentifier: string;
    shopDisplayName: string;
    address: ShopAddress;
    dataCollectionPeriod: DataCollectionPeriod[];
    sensorCameraCredentials: SensorCameraCredentials;
    collectData24Hours: boolean;
    fastLanerGroupIds: string[];
    isProduction?: boolean;
    hybridLocStoreKey?: string;
    gateways: Gateway[];
    components?: any[];

    // Recycle bin fields
    deleteDate?: string;
    permanentDeleteDate?: string;
}

export interface ReferenceShop {
    referenceShopId: string;
    gatewayName: string;
}

export interface CreateShopModel {
    referenceShop: ReferenceShop;
    shop: Shop;
}

export interface ShopAddress {
    id: string;
    shopId: string | null;
    street: string;
    postalCode: string;
    city: string;
    country: string;
    timeZone?: string;
    timeZoneName?: string;
}

export interface Module {
    discriminator?: string;
    id: string;
    gatewayId?: string;
    gateway?: Gateway | null;
    moduleId?: string | null;
    disabled?: boolean;
    deleteDate?: string;
    permanentDeleteDate?: string;
    status?: ModuleStatus;

    // TrolleyShelter properties
    impinjRfidReaderId?: string | null;
    impinjRfidReader?: Component;
    description?: string;
    capacity?: number;
    imagePath?: string;
    notificationRule?: any;

    // ZoneTracker properties
    zoneType?: number;
    zoneGates?: ZoneGate[];
    maxDwellTimeMinutes?: number;
    resetAfterInactivityMinutes?: number;

    // LineTracker properties
    lineGates?: LineGate[];

    // ShopAutomation properties
    incidentNotifications?: any[];
    accessConfigurations?: any[];
    snmpDevices?: any[];
    mediaPlayers?: any[];
    operationModesSchedules?: any[];
    activeOperationModes?: string[];
    shopAutomationStatus?: any;
    isOperationModeScheduleActive?: boolean;
    hasDisabledOperationModeSchedules?: boolean;
    logicServiceConfigId?: string | null;

    // SmartShelf properties
    smartShelfComponentId?: string | null;
    smartShelfComponent?: Component;

    // Common timing properties
    intervalInMinutes?: number;
}

// Legacy alias for backward compatibility
export interface DeviceModule extends Module {
    deviceId?: string | null;
    kind: string;
    shopId?: string;
}

export interface Gateway {
    id: string;
    shopId: string;
    shop?: Shop;
    deviceId: string;
    disabled: boolean;
    modules: DeviceModule[];
    status?: GatewayStatus;
    deviceCid?: string;
}

export interface LineGate {
    id?: string;
    sensorCameraId: string | null;
    elementId: string | null;
    elementName: string;
    isReversed: boolean;
    sensorCamera?: Component;
    backupSensorCameraId?: string | null;
    backupElementId?: string | null;
}

export interface ShopAutomationStatus {
    deviceId: string;
    reportedChangeDate: string;
    reportedOperationMode: string;
    reportedAlarmInterfaceMode?: string;
}

export interface ShopAutomation {
    id: string;
    moduleId: string;
    gateway: Gateway;
    shopAutomationStatus?: ShopAutomationStatus;
    isOperationModeScheduleActive: boolean;
    activeOperationModes: string[];
    hasDisabledOperationModeSchedules: boolean;
}

export interface ZoneGate {
    id?: string;
    sensorCameraId: string | null;
    elementId: string | null;
    isReversed: boolean;
    sensorCamera?: Component;
    backupSensorCameraId?: string | null;
    backupElementId?: string | null;
}

export type HealthStatusType = 'Unknown' | 'Good' | 'Warning' | 'Bad';

export interface GatewayStatus extends Status {
    id: string;
    unhealthy: boolean;
    deviceId: string;
    status: any;
    modules?: ModuleStatus[];
    ipAddress?: string;
    firmware?: string;
    isOnline: boolean;
    componentsStatus?: ComponentStatus[];
}

export interface CountCapacityData {
    currentCount: number;
    capacity: number;
}

// API Response types
export interface ApiResponse<T = any> {
    data: T;
    message?: string;
    success: boolean;
    errors?: string[];
}

export interface ApiError {
    message: string;
    status?: number;
    errors?: string[];
    properties?: string[];
}

// Component types
export interface Component {
    discriminator: string;
    id: string;
    shopId: string;
    gatewayId?: string;
    name: string;
    description?: string;
    ipAddress?: string;
    status?: ComponentStatus;
    configuration?: ComponentConfiguration;
}

export interface ComponentConfiguration {
    // Base configuration properties
    [key: string]: any;
    sensorCameraType?: string;
    lines?: string[];
    zones?: string[];
    port?: number;
    disabledAntennas?: number[];
    txPowerInDbm?: string;
    rxSensitivityInDbm?: number;
}

// Legacy component interface for backward compatibility
export interface ComponentBase {
    id: string;
    name: string;
    type: string;
    discriminator: string;
    shopId: string;
    deviceId: string;
    disabled: boolean;
    deleteDate?: string | null;
    createdAt?: string;
    updatedAt?: string;
    ipAddress?: string;
    status?: ComponentStatus;
}

// Module types
export interface ModuleBase {
    id: string;
    deviceId: string;
    shopId: string;
    disabled: boolean;
    deleteDate?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

// Base Status interface
export interface Status {
    changeDate: string;
    healthStatusType: HealthStatusType;
    error?: string;
}

export interface ModuleStatus extends Status {
    id: string;
    moduleId: string;
    status: any;
    [key: string]: any;
    discriminator: string;
    unhealthy: boolean;
    lastActivityDateTime?: string;
    firmware?: string;
    isOnline?: boolean;
}

export interface TrolleyShelterData {
    moduleId: string;
    deviceId: string;
    currentCount: number;
    capacity: number;
}

// Organisation interface
export interface Organisation {
    id: string;
    organisationId: string;
    name: string;
    description?: string;
    domain?: string;
    fastLanerGroupId?: string;
    fastLanerOrgId?: string;
    settings?: Record<string, any>;
    createdAt?: string;
    updatedAt?: string;
    ftpSettings?: FtpSettings;
    recycleBinRetentionDays?: number;
}

export interface FtpSettings {
    username: string;
    password: string;
    host: string;
    port?: number;
}

// Notification types
export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface NotificationOptions {
    title?: string;
    message: string;
    type: NotificationType;
    duration?: number;
    persistent?: boolean;
    actions?: Array<{
        label: string;
        action: () => void;
        style?: 'primary' | 'secondary';
    }>;
}

// Templates interface
export interface TemplateVariable {
    name: string;
    label: string;
}

// Health status types
export type HealthStatus = 'online' | 'offline' | 'warning' | 'error';

export interface HealthState {
    id: number;
    value: string;
    color: string;
    icon?: string;
}

export const HEALTH_STATES = Object.freeze({
    BATTERY_STATES: {
        good: {
            id: 'Good',
            value: 'goodHealth',
            icon: 'zmdi-battery-flash',
            color: 'success',
            order: 2
        },
        medium: {
            id: 'Medium',
            value: 'mediumHealth',
            icon: 'zmdi-battery-alert',
            color: 'warning',
            order: 1
        },
        bad: {
            id: 'Bad',
            value: 'badHealth',
            icon: 'zmdi-battery-alert',
            color: 'error',
            order: 0
        }
    }
});

export const STATUS_COLORS = Object.freeze({
    success: '#8AC926',
    warning: '#FFCA3A',
    error: '#FF595E',
    info: '#45B4D9'
});

// Dashboard widget types
export interface WidgetData {
    id: string;
    title: string;
    value: number | string;
    change?: number;
    changeType?: 'increase' | 'decrease' | 'neutral';
    unit?: string;
    status?: HealthStatus;
    lastUpdate?: Date;
}

// Form validation types
export interface ValidationRule {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    custom?: (value: any) => boolean | string;
}

export interface FormField {
    name: string;
    label: string;
    type: 'text' | 'email' | 'password' | 'number' | 'select' | 'checkbox' | 'textarea';
    value: any;
    rules?: ValidationRule[];
    options?: Array<{ label: string; value: any }>;
    disabled?: boolean;
    placeholder?: string;
}

// Theme types
export interface ThemeColors {
    primary: string;
    secondary: string;
    success: string;
    warning: string;
    error: string;
    info: string;
}

// Event System
export interface EventBusEvent {
    type: string;
    payload?: any;
    timestamp: Date;
}

// Component Status
export interface ComponentStatus extends Status {
    componentId: string;
    lastActivityDateTime?: string;
    firmware?: string;
    isOnline: boolean;
    ipAddress?: string;
}

// Dashboard Widget
export interface WidgetProps {
    shopIdName?: Shop;
    heading?: string;
    deviceId?: string;
    moduleId?: string;
    className?: string;
    hidden?: boolean;
}

// Statistics
export interface StatisticsFilter {
    shopId: string;
    start: string;
    end: string;
    interval?: number;
}

// Type guards
export const isUser = (obj: any): obj is User => {
    return obj && typeof obj.homeAccountId === 'string' && typeof obj.email === 'string';
};

export const isShop = (obj: any): obj is Shop => {
    return obj && typeof obj.id === 'string' && typeof obj.name === 'string';
};

export const isApiError = (obj: any): obj is ApiError => {
    return obj && typeof obj.message === 'string';
};

export enum ActiveLicenses {
    Basic = 'Basic',
    Rfid = 'Rfid',
    ShopAutomation = 'ShopAutomation',
    HybridLoc = 'HybridLoc',
    SmartShelf = 'SmartShelf',
    FastLaner = 'FastLaner'
}

export enum Role {
    SuperAdmin = 'SuperAdmin',
    OrganisationAdmin = 'OrganisationAdmin',
    UnitAdmin = 'UnitAdmin',
    StoreAdmin = 'StoreAdmin',
    User = 'User'
}

export const MODULES = Object.freeze({
    TROLLEY_SHELTER: 'trolleyShelter',
    ZONE_TRACKER: 'zoneTracker',
    LINE_TRACKER: 'lineTracker',
    SELF_SERVICE: 'shopAutomation',
    SMART_SHELF: 'smartShelf'
});

// Type definitions for TypeScript
export type ModuleType = (typeof MODULES)[keyof typeof MODULES];
